import React, { useEffect, useRef, useState, useCallback } from 'react';
import './Hero.css';

const TITLES = [
  'Data Scientist',
  'Data Analyst',
  'ML Engineer',
  'BI & Dashboard Builder'
];

const NOTES = [
  'Python, SQL, Power BI, Tableau',
  'Shopify analytics and forecasting',
  'Data-backed business decisions'
];

const Hero = () => {
  const canvasRef = useRef(null);
  const [titleIndex, setTitleIndex] = useState(0);
  const [displayTitle, setDisplayTitle] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = TITLES[titleIndex];
    let timeout;

    if (!isDeleting && displayTitle === current) {
      timeout = setTimeout(() => setIsDeleting(true), 1800);
    } else if (isDeleting && displayTitle === '') {
      setIsDeleting(false);
      setTitleIndex((prev) => (prev + 1) % TITLES.length);
    } else if (isDeleting) {
      timeout = setTimeout(() => {
        setDisplayTitle(current.substring(0, displayTitle.length - 1));
      }, 35);
    } else {
      timeout = setTimeout(() => {
        setDisplayTitle(current.substring(0, displayTitle.length + 1));
      }, 75);
    }

    return () => clearTimeout(timeout);
  }, [displayTitle, isDeleting, titleIndex]);

  const animIdRef = useRef(null);
  const particlesRef = useRef(null);

  const drawNetwork = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);
    const cw = canvas.offsetWidth;
    const ch = canvas.offsetHeight;

    // Reuse existing particles, rescale positions on resize
    if (!particlesRef.current) {
      particlesRef.current = Array.from({ length: 52 }, () => ({
        x: Math.random() * cw,
        y: Math.random() * ch,
        vx: (Math.random() - 0.5) * 0.45,
        vy: (Math.random() - 0.5) * 0.45,
        r: Math.random() * 2 + 1,
      }));
    } else {
      particlesRef.current.forEach((p) => {
        p.x = Math.min(p.x, cw);
        p.y = Math.min(p.y, ch);
      });
    }
    const particles = particlesRef.current;

    // Cancel any existing animation before starting new one
    if (animIdRef.current) cancelAnimationFrame(animIdRef.current);

    const animate = () => {
      ctx.clearRect(0, 0, cw, ch);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > cw) p.vx *= -1;
        if (p.y < 0 || p.y > ch) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0, 212, 255, 0.35)';
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 212, 255, ${0.12 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      animIdRef.current = requestAnimationFrame(animate);
    };
    animate();
  }, []);

  useEffect(() => {
    drawNetwork();
    const handleResize = () => drawNetwork();
    window.addEventListener('resize', handleResize);
    return () => {
      if (animIdRef.current) cancelAnimationFrame(animIdRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [drawNetwork]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="hero">
      <canvas ref={canvasRef} className="hero-canvas" />

      <div className="hero-floats">
        <div className="float-element float-1"><code>model.fit(X, y)</code></div>
        <div className="float-element float-2"><code>df.groupby().agg()</code></div>
        <div className="float-element float-4"><code>pipeline.deploy()</code></div>
      </div>

      <div className="container">
        <div className="hero-layout">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-dot" />
              Data Analytics and Machine Learning
            </div>

            <h1 className="hero-name">
              Turning data into
              <span className="name-highlight"> actionable insight.</span>
            </h1>

            <h2 className="hero-title">
              <span className="title-dynamic">{displayTitle}</span>
              <span className="title-cursor">|</span>
            </h2>

            <p className="hero-description">
              Detail-oriented and analytical-minded, I build clean analytics workflows, practical dashboards,
              and ML solutions that support data-driven decisions across business functions.
            </p>

            <div className="hero-buttons">
              <button className="btn" onClick={() => scrollToSection('projects')}>
                View Work
              </button>
              <button className="btn btn-outline" onClick={() => scrollToSection('contact')}>
                Contact
              </button>
            </div>

            <div className="hero-notes">
              {NOTES.map((note) => (
                <span key={note} className="hero-note">
                  {note}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;
